import React, { useEffect, useState, useRef } from "react";
import { Modal, Form, Row, Col, Button, Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  fetchPaymentsDashboard,
  markPaymentCollected,
  createManualPaymentTask,
  deleteManualPaymentTask,
  editPaymentTask,
  searchProjects,
} from "../api/service";

// F10 — Payments Dashboard. Combined with F8 manual-payment create/edit/delete.
// Visibility scoping is enforced server-side. The UI just shows what API returns.
//
// UI gates (frontend mirrors backend role checks for clean UX):
//   "Add Manual Payment"   — civil/sales/design head + team
//   "Edit" (3-dots menu)   — amit only for amount; F8 fields editable by F8-allowed roles
//   "Delete" (F8 row only) — F8-allowed roles, only when collected_amount = 0
//   "Mark Collected"       — payments@reminiscent.in (id 600) or admin role

const PAYMENTS_USER_ID = 600;
const AMIT_USER_ID = 48;

function getCurrentRole() {
  return localStorage.getItem("role") || "";
}
function getCurrentUserId() {
  const raw = localStorage.getItem("userid");
  const n = parseInt(raw, 10);
  return isNaN(n) ? null : n;
}

function formatAmount(value) {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (isNaN(n)) return value;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function formatSubtype(s) {
  if (s === "f3_payment") return "F3 Payment";
  if (s === "f8_manual") return "F8 Manual";
  if (s === "f9_revision") return "F9 Revision";
  return s || "-";
}

// Lightweight typeahead for project picker. Debounced 250ms.
function ProjectPicker({ value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchProjects(query);
        setOptions(res?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <Form.Control
        type="text"
        placeholder={placeholder || "Type project ID or customer name…"}
        value={value ? value.display : query}
        onChange={(e) => {
          if (value) onChange(null);
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (query.length > 0 || loading) && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: 4,
            maxHeight: 240,
            overflowY: "auto",
            marginTop: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {loading && (
            <div className="text-muted small p-2">Searching…</div>
          )}
          {!loading && options.length === 0 && query.length > 0 && (
            <div className="text-muted small p-2">No matches</div>
          )}
          {!loading &&
            options.map((opt) => (
              <div
                key={opt.id}
                style={{ padding: "6px 10px", cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(opt);
                  setQuery("");
                  setOpen(false);
                }}
                className="picker-row"
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              >
                <strong>Proj {String(opt.id).padStart(5, '0')}</strong>
                {opt.display ? <span className="text-muted ms-2">— {opt.display.replace(/^Proj\s+\d+_?/, '').replace(/_/g, ' ').trim()}</span> : null}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function PaymentsDashboard() {
  const role = getCurrentRole();
  const userId = getCurrentUserId();
  const F8_ALLOWED_ROLES = [
    "civil_team", "civil_head",
    "sales_team", "sales_head",
    "design_team", "design_head",
  ];
  const canCreateF8 = F8_ALLOWED_ROLES.includes(role);
  // Mark Collected/Refunded gated on:
  //   - payments@reminiscent.in (id 600) — primary collector
  //   - amit (id 48) — superuser, can intervene operationally
  //   - any user with role 'admin' (case-insensitive)
  const canMarkCollected =
    userId === PAYMENTS_USER_ID ||
    userId === AMIT_USER_ID ||
    (role && role.toLowerCase() === "admin");
  const isAmit = userId === AMIT_USER_ID;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    due_date_from: "",
    due_date_to: "",
    collection_date_from: "",
    collection_date_to: "",
    collection_done: "",
    project_id: "",
    description: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  // F8 create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createProject, setCreateProject] = useState(null);  // {id, display}
  const [createAmount, setCreateAmount] = useState("");
  const [createDueDate, setCreateDueDate] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createKind, setCreateKind] = useState("collect");  // 'collect' or 'refund'
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editing, setEditing] = useState(false);

  // Delete confirm
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Mark collected confirm
  const [markRow, setMarkRow] = useState(null);
  const [marking, setMarking] = useState(false);

  function loadRows() {
    setLoading(true);
    const params = {};
    Object.keys(appliedFilters).forEach((k) => {
      const v = appliedFilters[k];
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    fetchPaymentsDashboard(params)
      .then((res) => {
        const list = Array.isArray(res?.results) ? res.results : [];
        setRows(list);
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load payments dashboard");
        setRows([]);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  function handleFilterChange(field, value) {
    setFilters({ ...filters, [field]: value });
  }
  function applyFilters() {
    setAppliedFilters({ ...filters });
  }
  function clearFilters() {
    const cleared = {
      due_date_from: "", due_date_to: "",
      collection_date_from: "", collection_date_to: "",
      collection_done: "", project_id: "",
      description: "",
    };
    setFilters(cleared);
    setAppliedFilters({});
  }

  function openCreate() {
    setCreateProject(null);
    setCreateAmount("");
    setCreateDueDate("");
    setCreateDescription("");
    setCreateKind("collect");
    setShowCreate(true);
  }

  function submitCreate() {
    if (!createProject) {
      toast.error("Please select a project");
      return;
    }
    if (!createAmount || isNaN(parseFloat(createAmount)) || parseFloat(createAmount) <= 0) {
      toast.error("Expected amount must be a number greater than 0");
      return;
    }
    if (!createDueDate) {
      toast.error("Due date is required");
      return;
    }
    if (!createDescription || !createDescription.trim()) {
      toast.error("Description is required");
      return;
    }
    setCreating(true);
    createManualPaymentTask(createProject.id, {
      expected_amount: createAmount,
      due_date: createDueDate,
      description: createDescription.trim(),
      kind: createKind,
    })
      .then(() => {
        toast.success(createKind === "refund"
          ? "Manual refund task created"
          : "Manual payment task created");
        setShowCreate(false);
        loadRows();
      })
      .catch((err) => {
        toast.error(err?.detail || err?.message || "Failed to create");
      })
      .finally(() => setCreating(false));
  }

  function openEdit(row) {
    setEditRow(row);
    setEditAmount(row.expected_amount || "");
    setEditDueDate(row.due_date || "");
    setEditDescription(row.description || "");
  }

  function submitEdit() {
    if (!editRow) return;
    const isF8 = editRow.task_subtype === "f8_manual";
    const payload = {};

    // amit can edit amount on any payment task
    if (isAmit && editAmount !== "" && editAmount !== editRow.expected_amount) {
      const n = parseFloat(editAmount);
      if (isNaN(n) || n <= 0) {
        toast.error("Expected amount must be a number greater than 0");
        return;
      }
      payload.expected_amount = editAmount;
    }
    // F8 fields (due_date, description) can be edited by amit OR F8-allowed roles
    if (isF8 && (isAmit || canCreateF8)) {
      if (editDueDate && editDueDate !== editRow.due_date) {
        payload.due_date = editDueDate;
      }
      if (editDescription !== editRow.description) {
        if (!editDescription || !editDescription.trim()) {
          toast.error("Description cannot be empty");
          return;
        }
        payload.description = editDescription.trim();
      }
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setEditing(true);
    editPaymentTask(editRow.project_id, editRow.id, payload)
      .then(() => {
        toast.success("Task updated");
        setEditRow(null);
        loadRows();
      })
      .catch((err) => {
        toast.error(err?.detail || err?.message || "Failed to update");
      })
      .finally(() => setEditing(false));
  }

  function confirmDelete(row) {
    setDeleteRow(row);
  }
  function submitDelete() {
    if (!deleteRow) return;
    setDeleting(true);
    deleteManualPaymentTask(deleteRow.project_id, deleteRow.id)
      .then(() => {
        toast.success("Manual payment task deleted");
        setDeleteRow(null);
        loadRows();
      })
      .catch((err) => {
        toast.error(err?.detail || err?.message || "Failed to delete");
      })
      .finally(() => setDeleting(false));
  }

  function confirmMark(row) {
    setMarkRow(row);
  }
  function submitMark() {
    if (!markRow) return;
    setMarking(true);
    markPaymentCollected(markRow.project_id, markRow.id)
      .then(() => {
        toast.success(markRow.kind === "refund"
          ? "Marked as refunded"
          : "Marked as collected");
        setMarkRow(null);
        loadRows();
      })
      .catch((err) => {
        toast.error(err?.detail || err?.message || "Failed to mark collected");
      })
      .finally(() => setMarking(false));
  }

  // Compute totals from currently loaded rows (respects filters since rows is filtered server-side)
  const totals = (() => {
    let expected = 0;
    let collected = 0;
    rows.forEach((r) => {
      const e = Number(r.expected_amount || 0);
      const c = Number(r.collected_amount || 0);
      if (!isNaN(e)) expected += e;
      if (!isNaN(c)) collected += c;
    });
    return {
      expected: expected,
      collected: collected,
      outstanding: expected - collected,
    };
  })();

  function downloadCsv() {
    if (!rows.length) return;
    const headers = [
      "Project ID", "Project", "Task Type", "Refund?", "Description",
      "Expected (INR)", "Collected (INR)", "Due Date", "Collection Date",
      "Collection Done", "Status", "Immediate Payment",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
        return "\"" + s.replace(/"/g, "\"\"") + "\"";
      }
      return s;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        r.project_id,
        escape(r.project_label || ""),
        formatSubtype(r.task_subtype),
        r.kind === "refund" ? "Yes" : "No",
        escape(r.description || ""),
        r.expected_amount || "",
        r.collected_amount || "0",
        r.due_date || "",
        r.collection_date || "",
        r.collection_done ? "Yes" : "No",
        r.status || "",
        r.is_immediate_payment ? "Yes" : "No",
      ].join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = "payments_dashboard_" + ts + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Per-row permission helpers
  function canEditRow(row) {
    if (row.collection_done) return false;
    if (isAmit) return true;
    if (row.task_subtype === "f8_manual" && canCreateF8) return true;
    return false;
  }
  function canDeleteRow(row) {
    return canCreateF8
      && row.task_subtype === "f8_manual"
      && Number(row.collected_amount || 0) === 0
      && !row.collection_done;
  }
  function canMarkRow(row) {
    return canMarkCollected && !row.collection_done;
  }

  return (
    <div className="container-fluid px-4 py-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="mb-0">Payments Dashboard</h4>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Button variant="outline-secondary" size="sm" onClick={downloadCsv} disabled={!rows.length}>
            Download CSV
          </Button>
          {canCreateF8 && (
            <Button variant="primary" onClick={openCreate}>
              + Add Manual Payment
            </Button>
          )}
        </div>
      </div>

      {/* Totals chips */}
      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="card flex-grow-1" style={{ minWidth: 200 }}>
          <div className="card-body py-2 px-3">
            <div className="small text-muted">Total Expected</div>
            <div className="h5 mb-0">₹ {formatAmount(totals.expected)}</div>
          </div>
        </div>
        <div className="card flex-grow-1" style={{ minWidth: 200 }}>
          <div className="card-body py-2 px-3">
            <div className="small text-muted">Total Collected</div>
            <div className="h5 mb-0">₹ {formatAmount(totals.collected)}</div>
          </div>
        </div>
        <div className="card flex-grow-1" style={{ minWidth: 200 }}>
          <div className="card-body py-2 px-3">
            <div className="small text-muted">Outstanding</div>
            <div className="h5 mb-0 text-danger">₹ {formatAmount(totals.outstanding)}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-3">
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="small mb-1">Due date from</Form.Label>
              <Form.Control type="date" size="sm" value={filters.due_date_from}
                onChange={(e) => handleFilterChange("due_date_from", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Due date to</Form.Label>
              <Form.Control type="date" size="sm" value={filters.due_date_to}
                onChange={(e) => handleFilterChange("due_date_to", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Collection date from</Form.Label>
              <Form.Control type="date" size="sm" value={filters.collection_date_from}
                onChange={(e) => handleFilterChange("collection_date_from", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Collection date to</Form.Label>
              <Form.Control type="date" size="sm" value={filters.collection_date_to}
                onChange={(e) => handleFilterChange("collection_date_to", e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Collection done</Form.Label>
              <Form.Select size="sm" value={filters.collection_done}
                onChange={(e) => handleFilterChange("collection_done", e.target.value)}>
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Project</Form.Label>
              <ProjectPicker
                value={filters.project_id ? { id: filters.project_id, display: `Proj ${filters.project_id}` } : null}
                onChange={(opt) => handleFilterChange("project_id", opt?.id || "")}
                placeholder="(any) type id or name"
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small mb-1">Description contains</Form.Label>
              <Form.Control type="text" size="sm" placeholder="search description..."
                value={filters.description}
                onChange={(e) => handleFilterChange("description", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }} />
            </Col>
          </Row>
          <div className="mt-2 d-flex gap-2 justify-content-end">
            <Button variant="outline-secondary" size="sm" onClick={clearFilters}>Clear</Button>
            <Button variant="primary" size="sm" onClick={applyFilters}>Apply</Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover table-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>Status Flag</th>
              <th>Project</th>
              <th>Task Type</th>
              <th>Description</th>
              <th className="text-end">Expected (₹)</th>
              <th className="text-end">Collected (₹)</th>
              <th>Due Date</th>
              <th>Collection Date</th>
              <th>Collection Done</th>
              <th>Status</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center text-muted py-4">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="text-center text-muted py-4">No payment tasks found.</td></tr>
            )}
            {!loading && rows.map((row) => {
              const isRefund = row.kind === "refund";
              const showEdit = canEditRow(row);
              const showDelete = canDeleteRow(row);
              const showMark = canMarkRow(row);
              const anyAction = showEdit || showDelete || showMark;
              return (
                <tr key={row.id}>
                  <td>
                    {row.is_immediate_payment
                      ? <span className="badge bg-danger">Immediate Payment</span>
                      : <span className="text-muted small">-</span>}
                  </td>
                  <td>{row.project_label || ("Proj " + row.project_id)}</td>
                  <td>
                    {formatSubtype(row.task_subtype)}
                    {isRefund && <span className="badge bg-warning text-dark ms-1">Refund</span>}
                  </td>
                  <td title={row.description}>
                    {(row.description || "-").length > 60
                      ? row.description.slice(0, 60) + "…"
                      : row.description || "-"}
                  </td>
                  <td className="text-end">{formatAmount(row.expected_amount)}</td>
                  <td className="text-end">{formatAmount(row.collected_amount)}</td>
                  <td>{row.due_date || "-"}</td>
                  <td>{row.collection_date || "-"}</td>
                  <td>{row.collection_done ? "Yes" : "No"}</td>
                  <td>{row.status || "-"}</td>
                  <td>
                    {anyAction && (
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          variant="link"
                          size="sm"
                          className="p-0 text-muted"
                          style={{ textDecoration: "none", boxShadow: "none" }}
                          id={"actions-" + row.id}
                        >
                          ⋯
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {showMark && (
                            <Dropdown.Item onClick={() => confirmMark(row)}>
                              {isRefund ? "Mark Refunded" : "Mark Collected"}
                            </Dropdown.Item>
                          )}
                          {showEdit && (
                            <Dropdown.Item onClick={() => openEdit(row)}>Edit</Dropdown.Item>
                          )}
                          {showDelete && (
                            <Dropdown.Item className="text-danger" onClick={() => confirmDelete(row)}>
                              Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* === Create F8 Modal === */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add Manual Payment</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Project *</Form.Label>
            <ProjectPicker value={createProject} onChange={setCreateProject} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Type *</Form.Label>
            <div>
              <Form.Check inline type="radio" id="kind_collect" name="kind"
                label="Collect (customer owes us)"
                checked={createKind === "collect"}
                onChange={() => setCreateKind("collect")} />
              <Form.Check inline type="radio" id="kind_refund" name="kind"
                label="Refund (we owe customer)"
                checked={createKind === "refund"}
                onChange={() => setCreateKind("refund")} />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Expected Amount (₹) *</Form.Label>
            <Form.Control type="number" step="0.01" min="0.01"
              value={createAmount}
              onChange={(e) => setCreateAmount(e.target.value)}
              placeholder="e.g. 5000" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Due Date *</Form.Label>
            <Form.Control type="date" value={createDueDate}
              onChange={(e) => setCreateDueDate(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control as="textarea" rows={3} value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder="What is this payment for?" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
          <Button variant="primary" onClick={submitCreate} disabled={creating}>
            {creating ? "Creating..." : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* === Edit Modal === */}
      <Modal show={!!editRow} onHide={() => setEditRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Payment Task</Modal.Title></Modal.Header>
        <Modal.Body>
          {editRow && (
            <>
              <div className="small text-muted mb-3">
                Task #{editRow.id} • Project {editRow.project_id} • {formatSubtype(editRow.task_subtype)}
                {editRow.kind === "refund" && <span className="badge bg-warning text-dark ms-1">Refund</span>}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Expected Amount (₹) {isAmit ? "*" : "(amit only)"}</Form.Label>
                <Form.Control type="number" step="0.01" min="0.01"
                  value={editAmount}
                  disabled={!isAmit}
                  onChange={(e) => setEditAmount(e.target.value)} />
                {!isAmit && (
                  <Form.Text className="text-muted">
                    Only amit@reminiscent.in can edit the amount.
                  </Form.Text>
                )}
              </Form.Group>
              {editRow.task_subtype === "f8_manual" && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={3}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)} />
                  </Form.Group>
                </>
              )}
              {editRow.task_subtype !== "f8_manual" && !isAmit && (
                <div className="alert alert-warning small mb-0">
                  Only amit@reminiscent.in can edit auto-generated F3 payment tasks.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRow(null)} disabled={editing}>Cancel</Button>
          <Button variant="primary" onClick={submitEdit} disabled={editing}>
            {editing ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* === Delete Confirm Modal === */}
      <Modal show={!!deleteRow} onHide={() => setDeleteRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Manual Payment Task?</Modal.Title></Modal.Header>
        <Modal.Body>
          {deleteRow && (
            <>
              <p className="mb-2">
                This will soft-delete the task. It can only be deleted because no collection has been recorded.
              </p>
              <ul className="small mb-0">
                <li>Project: {deleteRow.project_id}</li>
                <li>Description: {deleteRow.description}</li>
                <li>Expected: ₹{formatAmount(deleteRow.expected_amount)}</li>
                <li>Due: {deleteRow.due_date}</li>
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRow(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={submitDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* === Mark Collected Confirm Modal === */}
      <Modal show={!!markRow} onHide={() => setMarkRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{markRow?.kind === "refund" ? "Mark as Refunded?" : "Mark Payment as Collected?"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {markRow && (
            <>
              <p className="mb-2">
                This will set <strong>collection_done = true</strong> with today's date and lock the task. This action cannot be undone.
              </p>
              <ul className="small mb-0">
                <li>Project: {markRow.project_id}</li>
                <li>Type: {formatSubtype(markRow.task_subtype)}{markRow.kind === "refund" ? " (Refund)" : ""}</li>
                <li>Expected: ₹{formatAmount(markRow.expected_amount)}</li>
                <li>Due: {markRow.due_date}</li>
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMarkRow(null)} disabled={marking}>Cancel</Button>
          <Button variant="success" onClick={submitMark} disabled={marking}>
            {marking ? "..." : (markRow?.kind === "refund" ? "Mark Refunded" : "Mark Collected")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
